import { useCallback, useState } from "react";

import { JobKind, QueryState } from "./types";
import { buildQueryParams } from "./utils";

import { useOmnichannelApi } from "~/OmnichannelApi";
import { Paths } from "~/OmnichannelApi/openapi";

type JobsData = Partial<Record<JobKind, Paths.JobsList.Responses.$200["jobs"]>>;
type Metadata = Partial<
  Record<JobKind, Paths.JobsList.Responses.$200["metadata"]>
>;

export function useJobBoardData(
  queryState: QueryState,
  config: { jobKind: JobKind; pageSize?: number }[],
  setFacets?: (
    facets: Paths.JobsList.Responses.$200["metadata"]["facets"]
  ) => void,
  onInitialLoad?: (queryState: QueryState) => void
) {
  const api = useOmnichannelApi();
  const [data, setData] = useState<JobsData>({});
  const [metadata, setMetadata] = useState<Metadata>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingByKind, setLoadingByKind] = useState<
    Partial<Record<JobKind, boolean>>
  >({});

  const initData = useCallback(() => {
    setIsInitialized(false);
    setLoadingByKind({});
    setData({});
    setMetadata({});

    Promise.all(
      config.map((item) => {
        const params = buildQueryParams(queryState, {
          jobKind: item.jobKind,
          take: item.pageSize ?? 10,
        });

        return api["jobs.list"](params).then((res) => ({
          kind: item.jobKind,
          jobs: res.data.jobs,
          metadata: res.data.metadata,
        }));
      })
    )
      .then((results) => {
        const jobsByKind: JobsData = {};
        const metadataByKind: Metadata = {};
        results.forEach(({ kind, jobs, metadata }) => {
          jobsByKind[kind] = jobs;
          metadataByKind[kind] = metadata;
        });
        setData(jobsByKind);
        setMetadata(metadataByKind);

        const firstKind = config[0]?.jobKind;
        const facets = metadataByKind[firstKind]?.facets;
        setFacets && facets && setFacets(facets);
        onInitialLoad && onInitialLoad(queryState);
      })
      .catch(console.error)
      .finally(() => {
        setIsInitialized(true);
      });
  }, [queryState, config, api, setFacets, onInitialLoad]);

  const loadMore = useCallback(
    (kind: JobKind) => {
      const kindMetadata = metadata[kind];
      if (!kindMetadata?.nextPageToken) {
        return;
      }

      setLoadingByKind((prev) => ({ ...prev, [kind]: true }));

      const params = buildQueryParams(queryState, {
        jobKind: kind,
        pageToken: kindMetadata.nextPageToken,
        take: config.find((item) => item.jobKind === kind)?.pageSize ?? 10,
      });

      api["jobs.list"](params)
        .then((res) => {
          setData((prev) => ({
            ...prev,
            [kind]: [...(prev[kind] || []), ...res.data.jobs],
          }));
          setMetadata((prev) => ({
            ...prev,
            [kind]: res.data.metadata,
          }));
        })
        .catch(console.error)
        .finally(() =>
          setLoadingByKind((prev) => ({ ...prev, [kind]: false }))
        );
    },
    [metadata, queryState, config, api]
  );

  const hasJobs = (kind?: JobKind) => {
    if (!kind) {
      return Object.values(data).some((jobs) => jobs.length > 0);
    }

    return (data[kind] || []).length > 0;
  };

  const isLoading = (kind: JobKind) => {
    return loadingByKind[kind] || false;
  };

  return {
    data,
    metadata,
    isInitialized,
    initData,
    loadMore,
    hasJobs,
    isLoading,
  };
}
